// src/pages/Pixels.jsx
import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Loader2, AlertCircle } from 'lucide-react';
import { PixelService } from '@/services/pixel.service';
import { PixelCard } from '@/components/features/pixels/PixelCard';
import { PixelCreationModal } from '@/components/features/pixels/PixelCreationModal';
import { StorageUtils } from '@/utils/storage';

const CLIENT_PIXEL_LIMIT = 1;
const PIXEL_ID_LENGTH = 15;

export function PixelsPage() {
  const { profile, updateProfile } = useAuth();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isAdmin, setIsAdmin] = useState(StorageUtils.isAdmin());
  const [showCreationModal, setShowCreationModal] = useState(false);
  const [newPixelId, setNewPixelId] = useState(null);

  // Update isAdmin when profile changes
  useEffect(() => {
    if (profile) {
      const adminStatus = profile.userRole === 'admin';
      setIsAdmin(adminStatus);
    }
  }, [profile]);

  // Determine if client user has reached pixel limit
  const currentPixelsCount = profile?.connectedPixels?.length || 0;
  const hasReachedLimit = !isAdmin && currentPixelsCount >= CLIENT_PIXEL_LIMIT;

  // Handle search input changes
  const handleSearchInput = (value) => {
    // Allow only digits
    const sanitizedValue = value.replace(/\D/g, '');
    setSearchInput(sanitizedValue);

    // Only trigger search if input is exactly 15 digits
    if (sanitizedValue.length === PIXEL_ID_LENGTH) {
      setSearchQuery(sanitizedValue);
    } else if (searchQuery) {
      // Clear search results if input is not 15 digits
      setSearchQuery('');
    }
  };

  // Query for pixels based on user role and search state
  const {
    data: pixelsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      'pixels',
      { search: searchQuery, connectedPixels: profile?.connectedPixels },
    ],
    queryFn: async () => {
      if (isAdmin && searchQuery) {
        // When admin is searching, use the search endpoint
        return PixelService.getPixels({ search: searchQuery });
      }

      // For both admin and client users, fetch connected pixels individually
      if (profile?.connectedPixels?.length) {
        return PixelService.getPixels({
          connectedPixels: profile.connectedPixels,
        });
      }

      // Return empty result if no connected pixels
      return {
        pixels: [],
        total: 0,
        page: 1,
        pageSize: 0,
        totalPages: 1,
      };
    },
    enabled: Boolean(profile),
  });

  const handleCreatePixel = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const response = await PixelService.createPixel();
      // Update profile with new pixel
      if (profile) {
        const updatedConnectedPixels = [
          ...(profile.connectedPixels || []),
          response.id,
        ];
        await updateProfile({
          ...profile,
          connectedPixels: updatedConnectedPixels,
        });
      }
      // Show success modal with the new pixel ID
      setNewPixelId(response.id);
      setShowCreationModal(true);
      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ['pixels'] });
    } catch (error) {
      console.error('Failed to create pixel:', error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="sl-pixels-page space-y-6">
      <div className="sl-pixels-header flex items-center justify-between">
        <div>
          <div className="sl-page-header flex items-center gap-3">
            <h1 className="sl-page-title text-2xl font-bold text-gray-900">
              Your Pixels
            </h1>
            <span className="sl-pixel-count text-sm bg-gray-100 text-gray-600 px-2 py-1 rounded-md font-medium">
              {isAdmin
                ? `${currentPixelsCount} pixels created`
                : `${currentPixelsCount} of ${CLIENT_PIXEL_LIMIT} pixel${CLIENT_PIXEL_LIMIT !== 1 ? 's' : ''} used`}
            </span>
          </div>
          <p className="sl-page-description mt-2 text-gray-600">
            {isAdmin
              ? searchQuery
                ? 'Search results for all pixels.'
                : 'Manage your connected pixels.'
              : 'Create and manage your tracking pixel.'}
          </p>
        </div>

        {(!hasReachedLimit || isAdmin) && (
          <button
            onClick={handleCreatePixel}
            disabled={isCreating}
            className="sl-create-pixel-btn inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 disabled:opacity-50">
            <Plus className="mr-2 h-4 w-4" />
            {isCreating ? 'Creating...' : 'Create Pixel'}
          </button>
        )}
      </div>

      <PixelCreationModal
        open={showCreationModal}
        onOpenChange={setShowCreationModal}
        pixelId={newPixelId}
      />

      {isAdmin && (
        <div className="sl-admin-search mb-6">
          <div className="sl-search-container relative flex items-center">
            <Search className="absolute left-4 h-5 w-5 text-gray-400 pointer-events-none" />
            {isLoading && searchQuery && (
              <Loader2 className="absolute left-4 h-5 w-5 animate-spin text-gray-400" />
            )}
            <input
              type="text"
              value={searchInput}
              placeholder="Enter a 15-digit pixel ID to search..."
              onChange={(e) => handleSearchInput(e.target.value)}
              maxLength={PIXEL_ID_LENGTH}
              className="sl-search-input w-full rounded-lg border-gray-300 pl-12 pr-4 py-2.5 focus:border-primary-500 focus:ring-primary-500 text-base"
            />
            {searchInput && searchInput.length !== PIXEL_ID_LENGTH && (
              <div className="sl-search-hint mt-2 flex items-center gap-1.5 text-sm text-gray-500 ml-4">
                <AlertCircle className="h-4 w-4" />
                Enter all {PIXEL_ID_LENGTH} digits to search
              </div>
            )}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="sl-loading-pixels grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, index) => (
            <div
              key={index}
              className="sl-pixel-skeleton h-24 animate-pulse rounded-lg bg-gray-100"
            />
          ))}
        </div>
      ) : error ? (
        <div className="sl-pixels-error rounded-lg bg-red-50 p-4 text-red-700">
          Error loading pixels. Please try again later.
        </div>
      ) : !pixelsData?.pixels?.length ? (
        <div className="sl-no-pixels rounded-lg bg-gray-50 p-4 text-center text-gray-500">
          {searchQuery
            ? 'No pixels found for your search.'
            : 'No pixels found.'}
        </div>
      ) : (
        <div className="sl-pixel-list grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {pixelsData.pixels.map((pixel) => (
            <PixelCard key={pixel.id} pixel={pixel} />
          ))}
        </div>
      )}
    </div>
  );
}
